import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Loader2,
  CheckCircle2,
  Circle,
  Clock,
  Calendar,
  Phone,
  Mail,
  MessageSquare,
  Play,
  Pause,
  UserPlus,
  Target,
  Users,
  DollarSign,
  CalendarCheck,
  AlertCircle,
  CheckSquare,
  Square,
  ChevronRight,
  Sparkles,
  Zap,
  RotateCcw,
  MessageCircle,
  PhoneIncoming,
  MailOpen,
  ArrowRight,
  Timer,
  Flag,
  History,
  CheckCircle,
  XCircle,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";

const API = "/api";

// Pursuit Loop Schedule - Days 1-10 (Call → Text → Email order)
const PURSUIT_SCHEDULE = [
  // Day 1 - Initial Contact (Call first, then Text, then Email)
  { day: 1, hour: 0, type: "call", label: "Place initial phone call", phase: "initial_engagement" },
  { day: 1, hour: 1, type: "sms", label: "Send SMS introduction", phase: "initial_engagement" },
  { day: 1, hour: 2, type: "email", label: "Send introduction email", phase: "initial_engagement" },
  { day: 1, hour: 5, type: "call", label: "Second call attempt", phase: "initial_engagement" },
  // Day 2-10 Follow-up Cadence
  { day: 2, hour: 0, type: "call", label: "Third call attempt", phase: "initial_engagement" },
  { day: 3, hour: 0, type: "call", label: "Call attempt", phase: "follow_up" },
  { day: 3, hour: 2, type: "sms", label: "Follow-up SMS", phase: "follow_up" },
  { day: 5, hour: 0, type: "call", label: "Call attempt", phase: "follow_up" },
  { day: 5, hour: 2, type: "email", label: "Follow-up email", phase: "follow_up" },
  { day: 7, hour: 0, type: "call", label: "Call attempt", phase: "follow_up" },
  { day: 10, hour: 0, type: "call", label: "Final call attempt", phase: "follow_up" },
];

// Contact Track Phases
const CONTACT_PHASES = [
  {
    id: "conversation",
    name: "Conversation",
    description: "Establish rapport and confirm relevance",
    tasks: [
      { id: "introduce", name: "Introduce yourself and company", priority: "high" },
      { id: "confirm_role", name: "Confirm the lead's role", priority: "high" },
      { id: "confirm_interest", name: "Confirm they are open to discussing the problem", priority: "high" },
    ]
  },
  {
    id: "discovery",
    name: "Discovery & Requirements",
    description: "Gather business requirements",
    tasks: [
      { id: "current_process", name: "Understand the lead's current process", priority: "high" },
      { id: "pain_points", name: "Identify operational pain points", priority: "high" },
      { id: "capture_requirements", name: "Capture requirements inside the CRM", priority: "medium" },
      { id: "desired_outcomes", name: "Determine desired outcomes", priority: "high" },
    ]
  },
  {
    id: "qualification",
    name: "Qualification & Opportunity",
    description: "Determine if lead is valid and schedule PM meeting",
    tasks: [
      { id: "decision_makers", name: "Identify decision makers", priority: "high" },
      { id: "budget", name: "Confirm budget range", priority: "high" },
      { id: "timeline", name: "Understand timeline", priority: "medium" },
      { id: "solution_fit", name: "Confirm solution fit", priority: "high" },
      { id: "schedule_pm", name: "Schedule meeting with Product Manager", priority: "high" },
      { id: "create_opportunity", name: "Create opportunity record", priority: "high" },
    ]
  }
];

export default function LeadWorkflowPage() {
  const navigate = useNavigate();
  const { leadId } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lead, setLead] = useState(null);
  const [workflow, setWorkflow] = useState(null);
  const [activeContactPhase, setActiveContactPhase] = useState("conversation");
  const [executingAction, setExecutingAction] = useState(null);

  const token = localStorage.getItem("admin_token");

  useEffect(() => {
    fetchLeadAndWorkflow();
  }, [leadId]);

  const fetchLeadAndWorkflow = async () => {
    try {
      const response = await fetch(`${API}/admin/leads/${leadId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setLead(data);
        
        // Check if workflow exists AND has the new schema (mode field)
        if (data.lead_workflow && data.lead_workflow.mode) {
          setWorkflow(data.lead_workflow);
          if (data.lead_workflow.mode === "contact" && data.lead_workflow.current_phase) {
            setActiveContactPhase(data.lead_workflow.current_phase);
          }
        } else if (data.lead_workflow && data.lead_workflow.scheduled_actions) {
          // Workflow exists but might be missing mode - preserve it and add mode
          const existingWorkflow = {
            ...data.lead_workflow,
            mode: data.lead_workflow.mode || "pursuit"
          };
          setWorkflow(existingWorkflow);
        } else {
          // No workflow exists - set to null, show "Start Pursuit" button (MANUAL START)
          setWorkflow(null);
        }
      }
    } catch (error) {
      console.error("Error fetching lead:", error);
      toast.error("Failed to load lead data");
    } finally {
      setLoading(false);
    }
  };

  const calculateScheduledTime = (startDate, day, hour) => {
    const date = new Date(startDate);
    date.setDate(date.getDate() + (day - 1));
    date.setHours(date.getHours() + hour);
    return date.toISOString();
  };

  const saveWorkflow = async (workflowData = workflow) => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/admin/leads/${leadId}/lead-workflow`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(workflowData),
      });
      if (response.ok) {
        toast.success("Progress saved");
      }
    } catch (error) {
      console.error("Error saving workflow:", error);
      toast.error("Failed to save progress");
    } finally {
      setSaving(false);
    }
  };

  const handleContactMade = async (contactType) => {
    try {
      const response = await fetch(`${API}/admin/leads/${leadId}/workflow/contact-made`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ contact_type: contactType }),
      });
      if (response.ok) {
        const data = await response.json();
        setWorkflow(data.workflow);
        setActiveContactPhase("conversation");
        toast.success("Contact made! Switched to Contact Track.");
      } else {
        toast.error("Failed to record contact");
      }
    } catch (error) {
      console.error("Error recording contact:", error);
      toast.error("Failed to record contact");
    }
  };

  const handleActionComplete = async (actionId) => {
    try {
      const response = await fetch(`${API}/admin/leads/${leadId}/workflow/action/${actionId}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ action_id: actionId, status: "completed" }),
      });
      if (response.ok) {
        // Update local state
        const updatedWorkflow = {
          ...workflow,
          scheduled_actions: workflow.scheduled_actions.map(action =>
            action.id === actionId ? { ...action, status: "completed", completed_at: new Date().toISOString() } : action
          )
        };
        setWorkflow(updatedWorkflow);
        toast.success("Action completed");
      }
    } catch (error) {
      console.error("Error completing action:", error);
      toast.error("Failed to complete action");
    }
  };

  const handleTogglePursuit = async () => {
    try {
      const response = await fetch(`${API}/admin/leads/${leadId}/workflow/pause`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setWorkflow({
          ...workflow,
          pursuit_paused: data.paused
        });
        toast.success(data.paused ? "Pursuit loop paused" : "Pursuit loop resumed");
      }
    } catch (error) {
      console.error("Error toggling pursuit:", error);
      toast.error("Failed to toggle pursuit");
    }
  };

  const handleTaskToggle = async (phaseId, taskId) => {
    try {
      const response = await fetch(`${API}/admin/leads/${leadId}/workflow/task/${phaseId}/${taskId}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        const updatedWorkflow = { ...workflow, contact_phases: data.phases };
        
        // Find current phase status
        const currentPhase = data.phases.find(p => p.id === phaseId);
        if (currentPhase?.status === "completed") {
          const phaseIndex = data.phases.findIndex(p => p.id === phaseId);
          if (phaseIndex < data.phases.length - 1) {
            setActiveContactPhase(data.phases[phaseIndex + 1].id);
            updatedWorkflow.current_phase = data.phases[phaseIndex + 1].id;
          }
        }
        
        setWorkflow(updatedWorkflow);
      }
    } catch (error) {
      console.error("Error toggling task:", error);
      toast.error("Failed to update task");
    }
  };

  const handleExecuteActionNow = async (actionId, actionType) => {
    setExecutingAction(actionId);
    try {
      const response = await fetch(`${API}/admin/leads/${leadId}/workflow/execute-action/${actionId}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Update local state with the result
          const updatedWorkflow = {
            ...workflow,
            scheduled_actions: workflow.scheduled_actions.map(action =>
              action.id === actionId ? data.action : action
            ),
            interaction_history: [...(workflow.interaction_history || []), {
              type: actionType,
              executed_at: new Date().toISOString(),
              result: data.execution_result,
              manual: true
            }]
          };
          setWorkflow(updatedWorkflow);
          
          if (actionType === "call") {
            toast.success(`Call initiated! ${data.execution_result?.call_id ? `Call ID: ${data.execution_result.call_id}` : ''}`);
          } else if (actionType === "email") {
            toast.success("Email prepared for sending");
          } else if (actionType === "sms") {
            toast.success("SMS queued for sending");
          }
        } else {
          toast.error(`Action failed: ${data.execution_result?.error || 'Unknown error'}`);
        }
      } else {
        toast.error("Failed to execute action");
      }
    } catch (error) {
      console.error("Error executing action:", error);
      toast.error("Failed to execute action");
    } finally {
      setExecutingAction(null);
    }
  };

  const handleConvertToOpportunity = async () => {
    try {
      const response = await fetch(`${API}/admin/leads/${leadId}`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ stage: "Opportunity" })
      });
      if (response.ok) {
        toast.success("Lead converted to Opportunity!");
        navigate(`/admin/leads/${leadId}`);
      }
    } catch (error) {
      toast.error("Failed to convert to opportunity");
    }
  };

  const handleMarkUnreachable = async () => {
    try {
      const response = await fetch(`${API}/admin/leads/${leadId}/workflow/mark-unreachable`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setWorkflow(data.workflow);
        toast.success("Lead marked as unreachable");
      }
    } catch (error) {
      console.error("Error marking unreachable:", error);
      toast.error("Failed to mark as unreachable");
    }
  };

  const handleStartPursuit = async () => {
    try {
      const response = await fetch(`${API}/admin/leads/${leadId}/workflow/start-pursuit`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setWorkflow(data.workflow);
          
          // Show result of immediate first action execution
          const firstResult = data.first_action_result;
          if (firstResult?.success) {
            if (firstResult.call_id) {
              toast.success(`Pursuit started! Call initiated to lead.`);
            } else {
              toast.success("Pursuit started! First action executed.");
            }
          } else if (firstResult?.error) {
            toast.warning(`Pursuit started, but first action failed: ${firstResult.error}`);
          } else {
            toast.success("Pursuit loop started!");
          }
        } else {
          toast.error(data.message || "Cannot start pursuit");
        }
      }
    } catch (error) {
      console.error("Error starting pursuit:", error);
      toast.error("Failed to start pursuit");
    }
  };

  const getNextPendingAction = () => {
    return workflow?.scheduled_actions?.find(a => a.status === "pending");
  };

  const getPursuitProgress = () => {
    if (!workflow?.scheduled_actions) return 0;
    const completed = workflow.scheduled_actions.filter(a => a.status === "completed").length;
    return Math.round((completed / workflow.scheduled_actions.length) * 100);
  };

  const getContactProgress = () => {
    if (!workflow?.contact_phases) return 0;
    const totalTasks = workflow.contact_phases.reduce((sum, p) => sum + p.tasks.length, 0);
    const completedTasks = workflow.contact_phases.reduce((sum, p) => 
      sum + p.tasks.filter(t => t.completed).length, 0);
    return Math.round((completedTasks / totalTasks) * 100);
  };

  const formatScheduledTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const getActionIcon = (type) => {
    switch (type) {
      case "call": return <Phone className="w-4 h-4" />;
      case "email": return <Mail className="w-4 h-4" />;
      case "sms": return <MessageSquare className="w-4 h-4" />;
      default: return <Circle className="w-4 h-4" />;
    }
  };

  const getActionColor = (type) => {
    switch (type) {
      case "call": return "text-emerald-400 bg-emerald-500/20";
      case "email": return "text-blue-400 bg-blue-500/20";
      case "sms": return "text-purple-400 bg-purple-500/20";
      default: return "text-zinc-400 bg-zinc-500/20";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#14A800]" />
      </div>
    );
  }

  const nextAction = getNextPendingAction();
  const currentContactPhase = workflow?.contact_phases?.find(p => p.id === activeContactPhase);

  return (
    <div className="min-h-screen bg-black text-white p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <button
          onClick={() => navigate(`/admin/leads/${leadId}`)}
          className="flex items-center gap-2 text-zinc-400 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to {lead?.first_name}'s Profile
        </button>

        <div className="flex items-start justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
              workflow?.mode === "pursuit" 
                ? "bg-gradient-to-br from-red-500 to-orange-500" 
                : workflow?.mode === "contact"
                ? "bg-gradient-to-br from-green-500 to-emerald-500"
                : "bg-gradient-to-br from-zinc-500 to-zinc-600"
            }`}>
              {workflow?.mode === "pursuit" ? (
                <RotateCcw className="w-7 h-7 text-white" />
              ) : workflow?.mode === "contact" ? (
                <MessageCircle className="w-7 h-7 text-white" />
              ) : (
                <AlertCircle className="w-7 h-7 text-white" />
              )}
            </div>
            <div>
              <h1 className="text-2xl font-bold">AI Lead Workflow</h1>
              <p className="text-zinc-400">{lead?.company_name || lead?.first_name + " " + lead?.last_name}</p>
            </div>
          </div>

          {/* Mode Badge */}
          <div className={`px-4 py-2 rounded-lg font-medium ${
            workflow?.mode === "pursuit" 
              ? "bg-red-500/20 text-red-400 border border-red-500/30" 
              : workflow?.mode === "contact"
              ? "bg-green-500/20 text-green-400 border border-green-500/30"
              : workflow?.mode === "unreachable"
              ? "bg-zinc-500/20 text-zinc-400 border border-zinc-500/30"
              : "bg-yellow-500/20 text-yellow-400 border border-yellow-500/30"
          }`}>
            {workflow?.mode === "pursuit" ? "🔴 Pursuit Loop" : 
             workflow?.mode === "contact" ? "🟢 Contact Track" : 
             workflow?.mode === "unreachable" ? "⚫ Unreachable" :
             "⏳ Not Started"}
          </div>
        </div>

        {/* Workflow Diagram */}
        <div className="bg-zinc-900/50 rounded-xl p-4 border border-zinc-800 mb-6">
          <div className="flex items-center justify-between text-sm">
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
              workflow?.mode === "pursuit" ? "bg-red-500/20 text-red-400" : "bg-zinc-800 text-zinc-500"
            }`}>
              <RotateCcw className="w-4 h-4" />
              <span>Pursuit Loop (Days 1-10)</span>
            </div>
            <ArrowRight className="w-5 h-5 text-zinc-600" />
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
              workflow?.mode === "contact" && activeContactPhase === "conversation" ? "bg-green-500/20 text-green-400" : "bg-zinc-800 text-zinc-500"
            }`}>
              <MessageCircle className="w-4 h-4" />
              <span>Conversation</span>
            </div>
            <ArrowRight className="w-5 h-5 text-zinc-600" />
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
              workflow?.mode === "contact" && activeContactPhase === "discovery" ? "bg-green-500/20 text-green-400" : "bg-zinc-800 text-zinc-500"
            }`}>
              <Target className="w-4 h-4" />
              <span>Discovery</span>
            </div>
            <ArrowRight className="w-5 h-5 text-zinc-600" />
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
              workflow?.mode === "contact" && activeContactPhase === "qualification" ? "bg-green-500/20 text-green-400" : "bg-zinc-800 text-zinc-500"
            }`}>
              <CalendarCheck className="w-4 h-4" />
              <span>Qualification</span>
            </div>
            <ArrowRight className="w-5 h-5 text-zinc-600" />
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-zinc-800 text-zinc-500">
              <Flag className="w-4 h-4" />
              <span>Opportunity</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Progress Card */}
            <div className="bg-zinc-900/50 rounded-xl p-6 border border-zinc-800">
              <h3 className="text-sm font-medium text-zinc-400 mb-4">
                {workflow?.mode === "pursuit" ? "PURSUIT PROGRESS" : "CONTACT PROGRESS"}
              </h3>
              <div className="flex items-center justify-center mb-4">
                <div className="relative w-32 h-32">
                  <svg className="w-full h-full transform -rotate-90">
                    <circle cx="64" cy="64" r="56" fill="none" stroke="#27272a" strokeWidth="8" />
                    <circle 
                      cx="64" cy="64" r="56" fill="none" 
                      stroke={workflow?.mode === "pursuit" ? "#ef4444" : "#14A800"}
                      strokeWidth="8"
                      strokeDasharray={`${(workflow?.mode === "pursuit" ? getPursuitProgress() : getContactProgress()) * 3.52} 352`}
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-3xl font-bold">
                      {workflow?.mode === "pursuit" ? getPursuitProgress() : getContactProgress()}%
                    </span>
                  </div>
                </div>
              </div>
              
              {workflow?.mode === "pursuit" && (
                <div className="text-center">
                  <p className="text-sm text-zinc-400">Day {workflow?.pursuit_day || 1} of 10</p>
                  <p className="text-xs text-zinc-500 mt-1">
                    {workflow.scheduled_actions?.filter(a => a.status === "completed").length || 0} of {workflow.scheduled_actions?.length || 0} actions completed
                  </p>
                </div>
              )}
            </div>

            {/* Contact Made Buttons - Only in Pursuit Mode */}
            {workflow?.mode === "pursuit" && (
              <div className="bg-zinc-900/50 rounded-xl p-6 border border-zinc-800">
                <h3 className="text-sm font-medium text-zinc-400 mb-4">CONTACT MADE?</h3>
                <p className="text-xs text-zinc-500 mb-4">Click when two-way communication occurs</p>
                <div className="space-y-2">
                  <button
                    onClick={() => handleContactMade("call_answered")}
                    className="w-full flex items-center gap-3 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 hover:bg-emerald-500/20 transition-colors"
                  >
                    <PhoneIncoming className="w-5 h-5 text-emerald-400" />
                    <span className="text-emerald-400">Phone Answered</span>
                  </button>
                  <button
                    onClick={() => handleContactMade("sms_reply")}
                    className="w-full flex items-center gap-3 p-3 rounded-lg bg-purple-500/10 border border-purple-500/30 hover:bg-purple-500/20 transition-colors"
                  >
                    <MessageSquare className="w-5 h-5 text-purple-400" />
                    <span className="text-purple-400">SMS Reply Received</span>
                  </button>
                  <button
                    onClick={() => handleContactMade("email_reply")}
                    className="w-full flex items-center gap-3 p-3 rounded-lg bg-blue-500/10 border border-blue-500/30 hover:bg-blue-500/20 transition-colors"
                  >
                    <MailOpen className="w-5 h-5 text-blue-400" />
                    <span className="text-blue-400">Email Reply Received</span>
                  </button>
                </div>
              </div>
            )}

            {/* Contact Phases Navigation - Only in Contact Mode */}
            {workflow?.mode === "contact" && (
              <div className="bg-zinc-900/50 rounded-xl p-6 border border-zinc-800">
                <h3 className="text-sm font-medium text-zinc-400 mb-4">CONTACT PHASES</h3>
                <div className="space-y-2">
                  {workflow.contact_phases?.map((phase) => (
                    <button
                      key={phase.id}
                      onClick={() => setActiveContactPhase(phase.id)}
                      className={`w-full flex items-center justify-between p-3 rounded-lg transition-all ${
                        activeContactPhase === phase.id 
                          ? "bg-green-500/20 border border-green-500/50" 
                          : "bg-zinc-800/50 hover:bg-zinc-800"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        {phase.status === "completed" ? (
                          <CheckCircle2 className="w-5 h-5 text-green-400" />
                        ) : phase.status === "in_progress" ? (
                          <Clock className="w-5 h-5 text-yellow-400" />
                        ) : (
                          <Circle className="w-5 h-5 text-zinc-600" />
                        )}
                        <span className={phase.status === "completed" ? "text-zinc-400" : "text-white"}>
                          {phase.name}
                        </span>
                      </div>
                      <ChevronRight className="w-4 h-4 text-zinc-500" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Pursuit Controls */}
            {workflow?.mode === "pursuit" && (
              <div className="bg-zinc-900/50 rounded-xl p-6 border border-zinc-800">
                <h3 className="text-sm font-medium text-zinc-400 mb-4">PURSUIT CONTROLS</h3>
                <div className="space-y-2">
                  <Button
                    onClick={handleTogglePursuit}
                    variant="outline"
                    className={`w-full ${workflow.pursuit_paused ? "border-green-500/30 text-green-400" : "border-yellow-500/30 text-yellow-400"}`}
                  >
                    {workflow.pursuit_paused ? (
                      <>
                        <Play className="w-4 h-4 mr-2" />
                        Resume Pursuit
                      </>
                    ) : (
                      <>
                        <Pause className="w-4 h-4 mr-2" />
                        Pause Pursuit
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={handleMarkUnreachable}
                    variant="outline"
                    className="w-full border-red-500/30 text-red-400"
                  >
                    <AlertCircle className="w-4 h-4 mr-2" />
                    Mark Unreachable
                  </Button>
                  <Button
                    onClick={handleStartPursuit}
                    variant="outline"
                    className="w-full border-orange-500/30 text-orange-400"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Restart Pursuit
                  </Button>
                </div>
              </div>
            )}

            {/* Start Pursuit Button - When no workflow exists or in Unreachable mode */}
            {(workflow?.mode === "unreachable" || !workflow) && (
              <div className="bg-zinc-900/50 rounded-xl p-6 border border-zinc-800">
                <h3 className="text-sm font-medium text-zinc-400 mb-4">
                  {workflow?.mode === "unreachable" ? "LEAD MARKED UNREACHABLE" : "START OUTREACH"}
                </h3>
                <p className="text-sm text-zinc-500 mb-4">
                  {workflow?.mode === "unreachable" 
                    ? "This lead was previously marked as unreachable. You can restart the pursuit loop to try again."
                    : "Click below to start the automated 10-day outreach sequence for this lead."
                  }
                </p>
                <div className="space-y-2">
                  <Button
                    onClick={handleStartPursuit}
                    className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
                  >
                    <Zap className="w-4 h-4 mr-2" />
                    {workflow?.mode === "unreachable" ? "Start Pursuit Loop Again" : "Start Pursuit Loop"}
                  </Button>
                </div>
              </div>
            )}

            {/* Interaction History - In Left Column */}
            {workflow?.interaction_history?.length > 0 && (
              <div className="bg-zinc-900/50 rounded-xl p-6 border border-zinc-800">
                <div className="flex items-center gap-2 mb-4">
                  <History className="w-4 h-4 text-zinc-400" />
                  <h3 className="font-medium text-white">Interaction History</h3>
                  <span className="text-xs text-zinc-500">({workflow.interaction_history.length})</span>
                </div>
                
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {[...workflow.interaction_history].reverse().map((interaction, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 rounded-lg bg-zinc-800/50">
                      <div className={`p-1.5 rounded-lg ${
                        interaction.type === "call" ? "bg-emerald-500/20" :
                        interaction.type === "email" ? "bg-blue-500/20" :
                        "bg-purple-500/20"
                      }`}>
                        {interaction.type === "call" ? <Phone className="w-3 h-3 text-emerald-400" /> :
                         interaction.type === "email" ? <Mail className="w-3 h-3 text-blue-400" /> :
                         <MessageSquare className="w-3 h-3 text-purple-400" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1">
                          <span className="text-sm text-white capitalize">{interaction.type}</span>
                          {interaction.result?.success ? (
                            <CheckCircle className="w-3 h-3 text-green-400" />
                          ) : (
                            <XCircle className="w-3 h-3 text-red-400" />
                          )}
                        </div>
                        <p className="text-xs text-zinc-500">
                          {new Date(interaction.executed_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* NO WORKFLOW STARTED YET */}
            {!workflow && (
              <div className="bg-zinc-900/50 rounded-xl p-8 border border-zinc-800 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-orange-500/10 flex items-center justify-center">
                  <Zap className="w-8 h-8 text-orange-400" />
                </div>
                <h2 className="text-xl font-bold mb-2">No Workflow Started</h2>
                <p className="text-zinc-400 mb-6 max-w-md mx-auto">
                  Start the Pursuit Loop to begin automated outreach to this lead. 
                  ZOEY will attempt to contact them over a 10-day period via calls, emails, and SMS.
                </p>
                <Button
                  onClick={handleStartPursuit}
                  className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
                >
                  <Zap className="w-4 h-4 mr-2" />
                  Start Pursuit Loop
                </Button>
              </div>
            )}

            {/* PURSUIT MODE VIEW */}
            {workflow?.mode === "pursuit" && (
              <div className="bg-zinc-900/50 rounded-xl p-6 border border-zinc-800">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-bold">Pursuit Schedule</h2>
                    <p className="text-zinc-400 text-sm">Automated outreach until contact is made</p>
                  </div>
                  {workflow.pursuit_paused && (
                    <span className="px-3 py-1 rounded-full bg-yellow-500/20 text-yellow-400 text-sm">
                      Paused
                    </span>
                  )}
                </div>

                {/* Next Action Highlight */}
                {nextAction && !workflow.pursuit_paused && (
                  <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/30">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${getActionColor(nextAction.type)}`}>
                          {getActionIcon(nextAction.type)}
                        </div>
                        <div>
                          <p className="font-medium text-white">Next Action: {nextAction.label}</p>
                          <p className="text-sm text-zinc-400">Scheduled: {formatScheduledTime(nextAction.scheduled_for)}</p>
                        </div>
                      </div>
                      <Button
                        onClick={() => handleExecuteActionNow(nextAction.id, nextAction.type)}
                        disabled={executingAction === nextAction.id}
                        className="bg-orange-500 hover:bg-orange-600 text-white disabled:opacity-50"
                      >
                        {executingAction === nextAction.id ? (
                          <>
                            <div className="w-4 h-4 mr-2 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            Executing...
                          </>
                        ) : (
                          <>
                            <Zap className="w-4 h-4 mr-2" />
                            Execute Now
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Timeline */}
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-zinc-400 mb-3">OUTREACH TIMELINE</h3>
                  {workflow.scheduled_actions?.map((action, idx) => (
                    <div
                      key={action.id}
                      className={`flex items-center gap-4 p-3 rounded-lg transition-all ${
                        action.status === "completed" ? "bg-green-500/10 border border-green-500/20" :
                        action.status === "skipped" ? "bg-zinc-800/30 opacity-50" :
                        nextAction?.id === action.id ? "bg-orange-500/10 border border-orange-500/30" :
                        "bg-zinc-800/50"
                      }`}
                    >
                      <div className={`p-2 rounded-lg ${getActionColor(action.type)}`}>
                        {getActionIcon(action.type)}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className={`font-medium ${action.status === "completed" ? "text-green-400" : action.status === "skipped" ? "text-zinc-500 line-through" : "text-white"}`}>
                            {action.label}
                          </span>
                          <span className="text-xs px-2 py-0.5 rounded bg-zinc-700 text-zinc-400">
                            Day {action.day}
                          </span>
                        </div>
                        <p className="text-xs text-zinc-500">{formatScheduledTime(action.scheduled_for)}</p>
                      </div>
                      <div>
                        {action.status === "completed" ? (
                          <CheckCircle2 className="w-5 h-5 text-green-400" />
                        ) : action.status === "skipped" ? (
                          <span className="text-xs text-zinc-500">Skipped</span>
                        ) : (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleActionComplete(action.id)}
                            className="text-zinc-400 hover:text-white"
                          >
                            Mark Done
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* CONTACT MODE VIEW */}
            {workflow?.mode === "contact" && currentContactPhase && (
              <div className="bg-zinc-900/50 rounded-xl p-6 border border-zinc-800">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <h2 className="text-xl font-bold">{currentContactPhase.name}</h2>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        currentContactPhase.status === "completed" ? "bg-green-500/20 text-green-400" :
                        currentContactPhase.status === "in_progress" ? "bg-yellow-500/20 text-yellow-400" :
                        "bg-zinc-700 text-zinc-400"
                      }`}>
                        {currentContactPhase.status === "completed" ? "Completed" :
                         currentContactPhase.status === "in_progress" ? "In Progress" : "Pending"}
                      </span>
                    </div>
                    <p className="text-zinc-400">{currentContactPhase.description}</p>
                  </div>
                </div>

                {/* Tasks */}
                <div className="space-y-2">
                  {currentContactPhase.tasks?.map((task) => (
                    <div
                      key={task.id}
                      onClick={() => handleTaskToggle(currentContactPhase.id, task.id)}
                      className={`flex items-center justify-between p-4 rounded-lg cursor-pointer transition-all ${
                        task.completed 
                          ? "bg-green-500/10 border border-green-500/20" 
                          : "bg-zinc-800/50 hover:bg-zinc-800"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        {task.completed ? (
                          <CheckSquare className="w-5 h-5 text-green-400" />
                        ) : (
                          <Square className="w-5 h-5 text-zinc-500" />
                        )}
                        <span className={task.completed ? "line-through text-zinc-500" : "text-white"}>
                          {task.name}
                        </span>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs ${
                        task.priority === "high" ? "bg-red-500/20 text-red-400" :
                        task.priority === "medium" ? "bg-yellow-500/20 text-yellow-400" :
                        "bg-zinc-700 text-zinc-400"
                      }`}>
                        {task.priority}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Convert to Opportunity Button - Only in Qualification phase */}
                {activeContactPhase === "qualification" && (
                  <div className="mt-6 pt-6 border-t border-zinc-800">
                    <Button
                      onClick={handleConvertToOpportunity}
                      className="w-full bg-[#14A800] hover:bg-[#14A800]/90 text-black font-semibold py-6"
                      disabled={!currentContactPhase.tasks?.every(t => t.completed)}
                    >
                      <Flag className="w-5 h-5 mr-2" />
                      Convert to Opportunity & Schedule PM Meeting
                    </Button>
                    {!currentContactPhase.tasks?.every(t => t.completed) && (
                      <p className="text-xs text-zinc-500 text-center mt-2">
                        Complete all tasks to convert
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ZOEY AI Actions - Separate Card */}
            <div className="bg-zinc-900/50 rounded-xl p-6 border border-zinc-800">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 rounded-lg bg-gradient-to-r from-yellow-500/20 to-orange-500/20">
                    <Sparkles className="w-4 h-4 text-yellow-400" />
                  </div>
                  <h3 className="font-medium text-white">ZOEY AI Actions</h3>
                </div>
                <span className="text-xs text-zinc-500">Click to open action panel</span>
              </div>
              
              <div className="grid grid-cols-4 gap-3">
                <button
                  onClick={() => navigate(`/admin/leads/${leadId}/call`)}
                  className="flex flex-col items-center gap-2 p-4 rounded-xl bg-zinc-800/50 hover:bg-zinc-800 border border-zinc-700/50 hover:border-emerald-500/30 transition-all group"
                >
                  <div className="p-3 rounded-full bg-emerald-500/10 group-hover:bg-emerald-500/20 transition-colors">
                    <Phone className="w-5 h-5 text-emerald-400" />
                  </div>
                  <span className="text-sm text-zinc-300">AI Call</span>
                </button>
                
                <button
                  onClick={() => navigate(`/admin/leads/${leadId}/email`)}
                  className="flex flex-col items-center gap-2 p-4 rounded-xl bg-zinc-800/50 hover:bg-zinc-800 border border-zinc-700/50 hover:border-blue-500/30 transition-all group"
                >
                  <div className="p-3 rounded-full bg-blue-500/10 group-hover:bg-blue-500/20 transition-colors">
                    <Mail className="w-5 h-5 text-blue-400" />
                  </div>
                  <span className="text-sm text-zinc-300">AI Email</span>
                </button>
                
                <button
                  onClick={() => navigate(`/admin/leads/${leadId}/text`)}
                  className="flex flex-col items-center gap-2 p-4 rounded-xl bg-zinc-800/50 hover:bg-zinc-800 border border-zinc-700/50 hover:border-purple-500/30 transition-all group"
                >
                  <div className="p-3 rounded-full bg-purple-500/10 group-hover:bg-purple-500/20 transition-colors">
                    <MessageSquare className="w-5 h-5 text-purple-400" />
                  </div>
                  <span className="text-sm text-zinc-300">AI Text</span>
                </button>
                
                <button
                  onClick={() => navigate(`/admin/leads/${leadId}/appointment`)}
                  className="flex flex-col items-center gap-2 p-4 rounded-xl bg-zinc-800/50 hover:bg-zinc-800 border border-zinc-700/50 hover:border-orange-500/30 transition-all group"
                >
                  <div className="p-3 rounded-full bg-orange-500/10 group-hover:bg-orange-500/20 transition-colors">
                    <Calendar className="w-5 h-5 text-orange-400" />
                  </div>
                  <span className="text-sm text-zinc-300">Schedule</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
